import React, { useContext, useEffect, useReducer, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { DeleteOutline, Edit, Code, Add, VpnKey } from "@material-ui/icons";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";

import PromptModal from "../../components/PromptModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import ForbiddenPage from "../../components/ForbiddenPage";
import TableRowSkeleton from "../../components/TableRowSkeleton";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: "24px",
    background: "#f5f7fa",
    minHeight: "100vh",
  },
  header: {
    marginBottom: "24px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "16px",
  },
  title: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#1a1a1a",
    margin: 0,
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  addButton: {
    padding: "10px 20px",
    background: theme.palette.primary.main,
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    "&:hover": {
      background: theme.palette.primary.dark,
      transform: "translateY(-1px)",
      boxShadow: "0 2px 8px rgba(59, 130, 246, 0.3)",
    },
  },
  tokenLink: {
    padding: "10px 20px",
    background: "#10b981",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    textDecoration: "none",
    "&:hover": {
      background: "#059669",
      transform: "translateY(-1px)",
      boxShadow: "0 2px 8px rgba(16, 185, 129, 0.3)",
    },
  },
  headerActions: {
    display: "flex",
    gap: "12px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  tableCard: {
    background: "white",
    borderRadius: "8px",
    padding: "20px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid #e8ecf1",
    overflow: "hidden",
  },
  tableContainer: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  tableHeader: {
    background: "#f9fafb",
    borderBottom: "2px solid #e8ecf1",
  },
  tableHeaderCell: {
    padding: "12px",
    textAlign: "left",
    fontSize: "12px",
    fontWeight: 600,
    color: "#666",
    whiteSpace: "nowrap",
  },
  tableRow: {
    borderBottom: "1px solid #e8ecf1",
    transition: "background 0.2s ease",
    "&:hover": {
      background: "#f9fafb",
    },
  },
  tableCell: {
    padding: "12px",
    fontSize: "13px",
    color: "#1a1a1a",
  },
  actionButtons: {
    display: "flex",
    gap: "6px",
    justifyContent: "center",
  },
  actionButton: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "6px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "4px",
    transition: "all 0.2s ease",
    "&:hover": {
      background: "#f0f0f0",
    },
  },
  emptyState: {
    textAlign: "center",
    padding: "40px 20px",
    color: "#999",
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_PROMPTS") {
    const prompts = action.payload;
    const newPrompts = [];

    prompts.forEach((prompt) => {
      const promptIndex = state.findIndex((p) => p.id === prompt.id);
      if (promptIndex !== -1) {
        state[promptIndex] = prompt;
      } else {
        newPrompts.push(prompt);
      }
    });

    return [...state, ...newPrompts];
  }

  if (action.type === "UPDATE_PROMPTS") {
    const prompt = action.payload;
    const promptIndex = state.findIndex((p) => p.id === prompt.id);

    if (promptIndex !== -1) {
      state[promptIndex] = prompt;
      return [...state];
    } else {
      return [prompt, ...state];
    }
  }

  if (action.type === "DELETE_PROMPT") {
    const promptId = action.payload;
    const promptIndex = state.findIndex((p) => p.id === promptId);
    if (promptIndex !== -1) {
      state.splice(promptIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const Prompts = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();

  const [prompts, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [promptModalOpen, setPromptModalOpen] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const companyId = user.companyId;

  useEffect(() => {
    async function fetchData() {
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useOpenAi) {
        toast.error("Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando.");
        setTimeout(() => {
          history.push(`/`);
        }, 500); // Reduzir delay de 1000ms para 500ms
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/prompt");
        dispatch({ type: "LOAD_PROMPTS", payload: data.prompts });
        setLoading(false);
      } catch (err) {
        toastError(err);
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const onPromptEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_PROMPTS", payload: data.prompt });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_PROMPT", payload: data.promptId });
      }
    };

    socket.on(`company-${companyId}-prompt`, onPromptEvent);
    return () => {
      socket.off(`company-${companyId}-prompt`, onPromptEvent);
    };
  }, [socket, companyId]);

  const handleOpenPromptModal = () => {
    setPromptModalOpen(true);
    setSelectedPrompt(null);
  };

  const handleClosePromptModal = () => {
    setPromptModalOpen(false);
    setSelectedPrompt(null);
  };

  const handleEditPrompt = (prompt) => {
    setSelectedPrompt(prompt);
    setPromptModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedPrompt(null);
  };

  const handleDeletePrompt = async (promptId) => {
    try {
      await api.delete(`/prompt/${promptId}`);
      toast.success("Prompt deletado com sucesso");
    } catch (err) {
      toastError(err);
    }
    setSelectedPrompt(null);
  };

  if (user.profile === "user") {
    return <ForbiddenPage />;
  }

  return (
    <div className={classes.container}>
      <ConfirmationModal
        title={
          selectedPrompt &&
          `${i18n.t("prompts.confirmationModal.deleteTitle")} ${selectedPrompt.name}?`
        }
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={() => handleDeletePrompt(selectedPrompt.id)}
      >
        {i18n.t("prompts.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <PromptModal
        open={promptModalOpen}
        onClose={handleClosePromptModal}
        promptId={selectedPrompt?.id}
      />

      {/* Header */}
      <div className={classes.header}>
        <h1 className={classes.title}>
          <Code style={{ fontSize: "28px" }} />
          {i18n.t("prompts.title")}
        </h1>
        <div className={classes.headerActions}>
          <a
            href="https://platform.openai.com/api-keys"
            target="_blank"
            rel="noopener noreferrer"
            className={classes.tokenLink}
          >
            <VpnKey style={{ fontSize: "18px" }} />
            Obter Token OpenAI
          </a>
          <button className={classes.addButton} onClick={handleOpenPromptModal}>
            <Add style={{ fontSize: "20px" }} />
            {i18n.t("prompts.buttons.add")}
          </button>
        </div>
      </div>

      {/* Tabela */}
      <div className={classes.tableCard}>
        {prompts.length === 0 && !loading ? (
          <div className={classes.emptyState}>
            <p>Nenhum prompt encontrado</p>
          </div>
        ) : (
          <div className={classes.tableContainer}>
            <table className={classes.table}>
              <thead className={classes.tableHeader}>
                <tr>
                  <th className={classes.tableHeaderCell}>
                    {i18n.t("prompts.table.name")}
                  </th>
                  <th className={classes.tableHeaderCell}>
                    {i18n.t("prompts.table.queue")}
                  </th>
                  <th className={classes.tableHeaderCell}>
                    {i18n.t("prompts.table.max_tokens")}
                  </th>
                  <th className={classes.tableHeaderCell} style={{ textAlign: "center" }}>
                    {i18n.t("prompts.table.actions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {prompts.map((prompt) => (
                  <tr key={prompt.id} className={classes.tableRow}>
                    <td className={classes.tableCell}>{prompt.name}</td>
                    <td className={classes.tableCell}>{prompt.queue?.name}</td>
                    <td className={classes.tableCell}>{prompt.maxTokens}</td>
                    <td className={classes.tableCell}>
                      <div className={classes.actionButtons}>
                        <button
                          className={classes.actionButton}
                          onClick={() => handleEditPrompt(prompt)}
                          title="Editar"
                        >
                          <Edit style={{ fontSize: "18px", color: "#3b82f6" }} />
                        </button>
                        <button
                          className={classes.actionButton}
                          onClick={() => {
                            setSelectedPrompt(prompt);
                            setConfirmModalOpen(true);
                          }}
                          title="Deletar"
                        >
                          <DeleteOutline style={{ fontSize: "18px", color: "#ef4444" }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {loading && <TableRowSkeleton columns={4} />}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Prompts;
